import { OfferItemList } from '@/features/offers/types';

import { StandardCard } from '@/components/cards/standard-card';
import { MyOffersEmptyState } from '../empty-state';
import { useCurrentUser } from '@/features/users/hooks';
import { useMemo } from 'react';
import { MyOffersTable } from '../table';
import { NotificationsCard } from '@/features/alerts';
import { BuyerAnalytics } from '../buyer-analytics';
import { Spinner } from '@/components/ui/spinner';

export type MyOffersItemsProps = {
  readonly offers: OfferItemList[];
  readonly isLoading?: boolean;
};

export function MyOffersItems({ offers, isLoading }: MyOffersItemsProps) {
  const user = useCurrentUser();

  const stats = useMemo(() => {
    if (!user) return { opportunitiesView: 0 };
    if (!offers) return { eoisSubmitted: 0, eoisAccepted: 0 };

    return {
      opportunitiesView: user.opportunity_view.length,
      eoisSubmitted: offers.length,
      eoisAccepted: offers.reduce((sum, offer) => sum + (offer.status.name === 'accepted' ? 1 : 0), 0),
    };
  }, [user, offers]);

  if (!user) return null;
  if (!offers) return null;

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <BuyerAnalytics stats={stats} isLoading={isLoading} />
        <NotificationsCard context="buyer" />
      </div>

      <StandardCard
        title="Your Submitted Expressions of Interest"
        description="Track the status of all EOIs you've submitted to sellers."
      >
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="text-primary-700 size-10" />
          </div>
        ) : offers?.length === 0 ? (
          <MyOffersEmptyState />
        ) : (
          <MyOffersTable offers={offers} />
        )}
      </StandardCard>
    </>
  );
}
